import ArrowTopRightOnSquareIcon from '@heroicons/react/24/outline/ArrowTopRightOnSquareIcon.js';
import DocumentIcon from '@heroicons/react/24/outline/DocumentIcon.js';
import type { ComponentType, SVGProps } from 'react';
import React, { useState } from 'react';
import { Button } from 'react-aria-components';

import { tv } from '../../tv';
import { Text } from '../text';

/**
 * A thing attached to a conversation: a document the agent produced, a file
 * a person sent. A raised card with a glyph tile on the left and the name
 * over the kind on the right, the shape every chat product gives a produced
 * document, so it reads as an object one can open rather than as words
 * someone said (Bubble is the accent fill; this is the surface and the
 * border). With `onPress` it is a real button, takes the interactive edge
 * and the lifted shadow on hover, and shows the open glyph at its trailing
 * edge; without, it is a label. The card owns its glyphs and sizes them
 * (icon.md). An image gives its `preview`, which fills the tile in place of
 * the glyph; one that fails to load leaves the glyph.
 *
 * The card lands inside rendered markdown, where the ground styles every
 * paragraph it contains; its lines are spans so no paragraph rule reaches
 * them and the card looks the same wherever it sits. It fills its line up
 * to a reading width, so a column of attachments aligns.
 */
const attachmentStyles = tv({
  slots: {
    root: [
      'ck:flex ck:w-full ck:max-w-sm ck:items-center ck:gap-3 ck:rounded-4 ck:border ck:border-border-divider',
      'ck:surface-raised ck:p-2 ck:pr-3 ck:text-left ck:text-inherit',
    ],
    tile: [
      'ck:grid ck:size-10 ck:shrink-0 ck:place-items-center ck:overflow-hidden ck:rounded-3',
      'ck:bg-neutral-a-3 ck:text-text-default',
    ],
    glyph: 'ck:size-5',
    thumbnail: 'ck:size-full ck:object-cover',
    body: 'ck:flex ck:min-w-0 ck:flex-1 ck:flex-col',
    open: 'ck:size-4 ck:shrink-0 ck:text-text-muted',
  },
  variants: {
    isPressable: {
      true: {
        root: [
          'ck:cursor-pointer ck:transition-chrome',
          'ck:hover:border-border-interactive ck:hover:shadow-md',
          'ck:pressed:shadow-sm ck:hover:pressed:shadow-sm',
          'ck:focus-visible:focus-ring',
        ],
      },
      false: {},
    },
  },
  defaultVariants: { isPressable: false },
});

export interface AttachmentProps {
  /** The file's name, as a person would say it. */
  name: string;
  /** What kind of thing it is, under the name: "PDF", "Image", "Policy draft". */
  kind?: string;
  /** The glyph for the kind, in the tile; a document by default. */
  glyph?: ComponentType<SVGProps<SVGSVGElement>>;
  /** An image of the file, shown in the tile instead of the glyph. */
  preview?: string;
  /** Opens the attachment; the card becomes a button. */
  onPress?: () => void;
  id?: string;
}

export const Attachment = React.forwardRef<HTMLElement, AttachmentProps>(function Attachment(
  { name, kind, glyph: Glyph = DocumentIcon, preview, onPress, ...props },
  ref,
) {
  const [failedPreview, setFailedPreview] = useState<string>();
  const isPressable = onPress !== undefined;
  const { root, tile, glyph, thumbnail, body, open } = attachmentStyles({ isPressable });
  const inner = (
    <>
      <span className={tile()}>
        {preview && preview !== failedPreview ? (
          <img
            src={preview}
            alt=""
            className={thumbnail()}
            onError={() => setFailedPreview(preview)}
          />
        ) : (
          <Glyph className={glyph()} aria-hidden="true" />
        )}
      </span>
      <span className={body()}>
        <Text as="span" size="sm" weight="medium" isTruncated>
          {name}
        </Text>
        {kind ? (
          <Text as="span" size="xs" color="muted" isTruncated>
            {kind}
          </Text>
        ) : null}
      </span>
      {isPressable ? <ArrowTopRightOnSquareIcon className={open()} aria-hidden="true" /> : null}
    </>
  );
  if (onPress) {
    return (
      <Button
        {...props}
        ref={ref as React.Ref<HTMLButtonElement>}
        className={root()}
        onPress={onPress}
        data-component="attachment"
      >
        {inner}
      </Button>
    );
  }
  return (
    <span
      {...props}
      ref={ref as React.Ref<HTMLSpanElement>}
      className={root()}
      data-component="attachment"
    >
      {inner}
    </span>
  );
});
